import { useMemo } from 'react';
import type {} from '@react-three/fiber';
import { Text } from '@react-three/drei';
import { squareToVec3 } from './scene.js';

/**
 * Render the a-h / 1-8 file-rank labels at the perimeter of the board. Used
 * by `ChessBoard` when the player has enabled `showCoords`. File letters run
 * along the bottom row, rank numbers run along the left column — the layout
 * a printed chess diagram uses, so the kid reads it as "this is e2".
 *
 * Each label sits just above the board surface (`y = 0.06`) and just outside
 * the square (`x` or `z` offset by ~0.55 from the edge square) so it never
 * clips a piece. The text is rendered via drei's `<Text>` (MSDF), which
 * respects WebGL alpha so the dark labels stay legible against the lighter
 * board squares underneath.
 */
export function CoordinateLabels() {
  const labels = useMemo(() => {
    const out: Array<{ pos: [number, number, number]; text: string; rotation?: [number, number, number] }> = [];
    for (let file = 0; file < 8; file++) {
      // Rank 1 is the NEAR edge (z = -3.5 in scene coords: `squareToVec3`, with
      // the camera on the -z side). The letter sits just outside that rank, so
      // it is z - 0.55. (This was z + 0.55, which put every letter ON the
      // rank-2 squares, under the pawns.)
      const square = `${String.fromCharCode(97 + file)}1`;
      const [x, , z] = squareToVec3(square);
      out.push({
        pos: [x, 0.06, z - 0.55],
        text: String.fromCharCode(97 + file),
      });
    }
    for (let rank = 1; rank <= 8; rank++) {
      // The a-file is at x = +3.5 (the screen-LEFT edge from the camera). The
      // number sits just outside it.
      const square = `a${rank}`;
      const [x] = squareToVec3(square);
      out.push({
        pos: [x + 0.55, 0.06, 3.5 - (8 - rank)],
        text: String(rank),
      });
    }
    return out;
  }, []);

  return (
    <group data-testid="coordinate-labels">
      {labels.map((l) => (
        <Text
          key={`coord-${l.text}`}
          position={l.pos}
          fontSize={0.32}
          color="#1a1a1a"
          anchorX="center"
          anchorY="middle"
          // Lay the text flat (-PI/2 about X), then turn it PI in the board
          // plane. The camera looks along +z, so a flat label with no turn
          // reads upside-down from the player's seat; Euler order is XYZ, so
          // the Z term is applied first, before the glyphs are laid down.
          rotation={[-Math.PI / 2, 0, Math.PI]}
        >
          {l.text}
        </Text>
      ))}
    </group>
  );
}
